import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import { scroller } from "react-scroll";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS / PURCHASE-FUNNEL SURFACE.** The lower "Take the Free Trial"
 * CTA block on /free-trial. Styling-only conversion: no handler, prop,
 * condition, DOM node, image URL or scroll target moved.
 *
 * Conversions follow the batch conventions documented at the head of
 * `FreeTrial/Top.tsx` (spacing literals in `sx`, `theme.spacing` kept inside
 * `styled`, breakpoint helpers for 600/960, the literal 769px query kept as-is,
 * empty `@media` blocks dropped as dead rules).
 *
 * DEAD RULE DROPPED (guide §1.2 row 1): `noUnderline` was declared in the v4
 * `useStyles` object but never referenced from the JSX.
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare host elements, which have no `sx` prop
 * ---------------------------------------------------------------------- */

// was: useStyles().background
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  width: "auto",
  display: "flex",
  boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.8)",
  height: "auto",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
  },
  "@media (min-width:769px)": {
    height: "auto",
  },
  [theme.breakpoints.up("md")]: {
    height: "80vh",
  },
}));

// was: useStyles().skinWrapper
const SkinWrapper = styled("div")({
  position: "relative",
});

// was: useStyles().appImage
const AppImage = styled("img")(({ theme }) => ({
  minHeight: "500px",
  maxHeight: "700px",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().appImageHalf
const AppImageHalf = styled("img")(({ theme }) => ({
  minHeight: "250px",
  maxHeight: "450px",
  marginBottom: theme.spacing(0),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().dashboardScroller
const DashboardScroller = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "64px",
  left: "-1px",
  height: "365px",
  width: "258px",
  padding: "7% 7% 0 7%",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    width: "296px",
    top: "95px",
    left: "22px",
    padding: "0",
    height: "580px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().centerBoxLeft
const centerBoxLeftSx = (theme: Theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "40px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "40px",
  },
  // PARITY RESTORE — v4's `@media (min-width:769px) { height: auto }` is dropped,
  // not ported. In an `sx` object it cannot keep its v4 position: MUI's
  // `styleFunctionSx` seeds its output with an empty scaffold of the theme's
  // OWN breakpoint keys, so every `theme.breakpoints.*` block sits at the front
  // of the emitted object and any literal `@media` string is appended AFTER it.
  // 769 therefore landed after the 960 block and its `height: auto` beat
  // `height: 80vh` at every desktop width, collapsing the hero box and — because
  // it is `justifyContent: center` — riding the whole hero up the page. Measured
  // on the identical construct in `FreeTrial/Top.tsx` at 1440 (700px instead of
  // 900px, hero ~98.5px high).
  // The block is provably inert in v4: below 769 it does not apply; from 769 to
  // 959 it re-asserts the `height: auto` the flat rule and the `sm` block
  // already set; from 960 up v4's later `md` block overrode it. Dropping it
  // reproduces v4 exactly at every width (guide §1.2 row 1).
  // NB the sibling `Background` styled() above keeps its 769 block verbatim —
  // `styled` is raw emotion with no scaffold, so source order survives there.
  [theme.breakpoints.up("md")]: {
    height: "80vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
});

// was: useStyles().centerBoxRight
const centerBoxRightSx = (theme: Theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "16px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "32px",
  },
  // PARITY RESTORE — same dropped-`@media (min-width:769px)` case as
  // `centerBoxLeftSx` above; see the note there for the measurement and proof.
  [theme.breakpoints.up("md")]: {
    height: "80vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
});

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  marginTop: "24px",
  marginBottom: "24px",
  letterSpacing: "-2px",
  fontSize: "4.4rem",
  lineHeight: "3rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  // PARITY RESTORE — the responsive `& span` rule is nested INSIDE the `& span`
  // block rather than left in the `sm` block where v4 declared it. Same
  // `styleFunctionSx` scaffold as the `centerBox*` note above: breakpoint keys
  // are emitted first, so a plain nested selector written at the top level of an
  // `sx` object comes out AFTER `@media { & span }` and, being an equal-
  // specificity (0,1,1) rule later in the sheet, wins at every width — the
  // subheading renders at its xs size on desktop (measured on the identical
  // construct in `FreeTrial/Top.tsx`: 48px/51.2px instead of 64px/67.2px).
  // Nesting the breakpoint under the selector emits exactly v4's two rules in
  // v4's order, so the cascade no longer depends on key order at all.
  "& span": {
    fontSize: "3rem",
    lineHeight: "3.2rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "4rem",
      lineHeight: "4.2rem",
    },
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "6.4rem",
    lineHeight: "4.6rem",
  },
});

// was: useStyles().button
// `color: "text.primary"` is NOT in the v4 rule — it restores the v4 Button
// default colour that v5+ removed (guide §2, "Button default color"); without
// it this CTA renders lime-on-lime.
const buttonSx = (theme: Theme) => ({
  marginTop: "16px",
  width: "100%",
  fontSize: "1.2rem",
  padding: "16px",
  marginLeft: 0,
  marginRight: 0,
  color: "text.primary",
  [theme.breakpoints.up("sm")]: {
    width: "80%",
  },
});

// was: useStyles().buttonSubText
const buttonSubTextSx = (theme: Theme) => ({
  textAlign: "center",
  marginTop: "8px",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    textAlign: "left",
    marginLeft: "40px",
  },
});

// was: useStyles().imageContainer
const imageContainerSx = (theme: Theme) => ({
  height: "365px",
  [theme.breakpoints.up("sm")]: {
    height: "515px",
  },
});

const Trial = () => {
  const theme = useTheme();

  // Complementary <Hidden xsDown> / <Hidden smUp> pair — one query drives both
  // branches so exactly one renders at every width AND with no matchMedia
  // (jsdom / prerender), reproducing v4's single `withWidth()` value.
  // See the fuller note in FreeTrial/Top.tsx.
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  function scrollToForm() {
    scroller.scrollTo("onboarding-form", {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
    });
  }

  return (
    <>
      <Background>
        <Container maxWidth="md" fixed>
          {/* v5+ Grid: `item` is gone and the breakpoint props collapse into
              `size`. No `spacing` on this container, so widths are unchanged. */}
          <Grid container>
            <Grid size={{ xs: 12, md: 7 }} sx={centerBoxLeftSx}>
              <Box>
                <Box>
                  <Typography variant="h1" sx={titleSx}>
                    <span>Take the</span>
                    <br />
                    Free Trial
                  </Typography>
                  <Button onClick={scrollToForm} sx={buttonSx}>
                    Create a free account
                  </Button>
                  <Typography variant="body1" sx={buttonSubTextSx}>
                    No Payment Required for Free Trial
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={centerBoxRightSx}>
              <SkinWrapper>
                {/* Visible SM, MD, L, XL — was <Hidden xsDown> */}
                {smUp && (
                  <AppImage
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_100.png"
                    }
                    alt="FarmFLiX App"
                  />
                )}
                {/* Visible XS only — was <Hidden smUp> */}
                {!smUp && (
                  <AppImageHalf
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_75.png"
                    }
                    alt="FarmFLiX App"
                  />
                )}
                <DashboardScroller>
                  <Carousel
                    useKeyboardArrows={true}
                    showArrows={false}
                    interval={3000}
                    showThumbs={false}
                    infiniteLoop={true}
                    showStatus={false}
                    autoPlay={true}
                    showIndicators={false}
                    emulateTouch={false}
                    swipeable={false}
                    transitionTime={500}
                  >
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/1.png"
                        }
                        alt="App Dashboard"
                      />
                    </Box>
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/2.png"
                        }
                        alt="App Currently Playing"
                      />
                    </Box>
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/4.png"
                        }
                        alt="Clydes Farm Series"
                      />
                    </Box>
                  </Carousel>
                </DashboardScroller>
              </SkinWrapper>
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default Trial;
